import { useEffect, useRef, useState } from "react";
import { Box, Center, Image, Loader, Stack, Text, Group } from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";

import { api } from "@utils/index";
import { useLocale } from "@/apps/@components";
import { CampaignButton } from "../components";
import { findWallpaper } from "../wallpapers";
import {
	useWallpaperFlow,
	type WallpaperColor,
	type WallpaperFont,
} from "./WallpaperFlowContext";
import { logWallpaperEvent, logWallpaperPage } from "../wallpaperLog";
import { WallpaperWebApi } from "@utils/api/wallpaper/web-client.api";
import { getStoredFrom, isLiffFlow } from "../wallpaperParams";

const COMPOSITE_WIDTH = 540;
const COMPOSITE_RATIO = 36.1 / 78.1;
const COMPOSITE_HEIGHT = Math.round(COMPOSITE_WIDTH / COMPOSITE_RATIO);

const FONT_META = {
	classic: {
		family: "SweetSansPro",
		stack: "'SweetSansPro', sans-serif",
		weight: 700,
		ratio: 6.1 / 36.1,
	},
	handwrite: {
		family: "WorkhorseScript",
		stack: "'WorkhorseScript', cursive",
		weight: 100,
		ratio: 5.4 / 36.1,
	},
} as const;

const SHADOW_OFFSET_RATIO = 4 / 1080;
const SHADOW_BLUR_RATIO = 10 / 1080;

const TEXT_COLOR: Record<WallpaperColor, string> = {
	black: "#000000",
	white: "#FFFFFF",
};

const SHADOW_COLOR: Record<WallpaperColor, string> = {
	black: "#FFFFFFE5",
	white: "#000000E5",
};

interface DownloadStateParams {
	wallpaperId: number | null;
	text: string;
	font: WallpaperFont;
	color: WallpaperColor;
}

/** 預先載入圖片（帶 crossOrigin 讓 canvas 匯出不被 taint）。 */
function preloadImage(src: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const img = new window.Image();
		img.crossOrigin = "anonymous";
		img.onload = () => resolve();
		img.onerror = () => reject(new Error(`load image failed: ${src}`));
		img.src = src;
	});
}

const OUTPUT_TYPE = "image/jpeg";
const OUTPUT_QUALITY = 0.9;
const OUTPUT_FILENAME = "wallpaper.jpg";

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) =>
				blob ? resolve(blob) : reject(new Error("canvas.toBlob 回傳 null")),
			OUTPUT_TYPE,
			OUTPUT_QUALITY,
		);
	});
}

/**
 * 桌布完成頁（Wallpaper-3）。
 * 進頁時用 html2canvas 把「桌布圖 + 疊字」合成成 PNG，
 * 以 multipart/form-data 上傳到 /wallpaper/upload（帶登入換來的 token），
 * 顯示後端回傳的 image_url。
 */
export default function WallpaperDownloadPage() {
	const { t } = useLocale();
	const navigate = useNavigate();
	const location = useLocation();
	const flow = useWallpaperFlow();
	const { locale } = useLocale();

	const langSuffix = locale === "en" ? "?lang=en" : "";

	// 優先用 text-setting 快照的參數（text 已被清空時仍讀得到），否則退回 context
	const stateParams = (location.state ?? null) as DownloadStateParams | null;
	const wallpaperId = stateParams?.wallpaperId ?? flow.wallpaperId;
	const text = stateParams?.text ?? flow.text;
	const font = stateParams?.font ?? flow.font;
	const color = stateParams?.color ?? flow.color;
	const wallpaper = findWallpaper(wallpaperId);

	const compositeRef = useRef<HTMLDivElement>(null);
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [status, setStatus] = useState<"loading" | "ready" | "error">(
		"loading",
	);
	// 長按儲存 overlay 是否開啟
	const [showSave, setShowSave] = useState(false);
	const startedRef = useRef(false);

	useEffect(() => {
		if (startedRef.current) return;
		startedRef.current = true;

		// 缺少必要參數（例如直接進網址 / reload 後 state 遺失）→ 退回選圖
		if (!wallpaper || !text) {
			navigate(
				{ pathname: "/wallpaper/start", search: location.search },
				{ replace: true },
			);
			return;
		}

		(async () => {
			try {
				// 1) 確保桌布圖與字型都就緒，html2canvas 才畫得出正確結果
				await preloadImage(wallpaper.src);
				try {
					await document.fonts.load(`100px "${FONT_META[font].family}"`);
					await document.fonts.ready;
				} catch {
					// 字型載入失敗就用系統字，不阻斷合成
				}
				// 等一個 frame 確保離屏節點已 paint
				await new Promise((r) => requestAnimationFrame(() => r(null)));

				const node = compositeRef.current;
				if (!node) throw new Error("找不到合成節點");

				// 2) html2canvas 只負責畫桌布底圖（離屏節點不含文字）
				const canvas = await html2canvas(node, {
					useCORS: true,
					backgroundColor: null,
					scale: 2,
					width: COMPOSITE_WIDTH,
					height: COMPOSITE_HEIGHT,
				});

				// 3) 文字改用原生 canvas 繪製，位置才精準
				//    （html2canvas 對文字 baseline 有偏差，且不吃 translate(-50%,-50%)）
				//    textAlign=center + textBaseline=middle → 文字中心精準對齊 textPos。
				const ctx = canvas.getContext("2d");
				if (ctx && text) {
					// html2canvas 回傳的 context 仍殘留 scale(2) 變換，先重設為單位矩陣，
					// 之後才能用「裝置像素座標」精準定位，否則文字會被畫到畫布外。
					ctx.setTransform(1, 0, 0, 1, 0, 0);
					const fontMeta = FONT_META[font];
					const outScale = canvas.width / COMPOSITE_WIDTH; // 對應 html2canvas 的 scale
					const fontSizePx = COMPOSITE_WIDTH * fontMeta.ratio * outScale;
					ctx.font = `${fontMeta.weight} ${fontSizePx}px ${fontMeta.stack}`;
					ctx.fillStyle = TEXT_COLOR[color];
					// 陰影以畫布寬為基準（canvas.width = 1080），等於設計稿的 0 4px 10px
					ctx.shadowColor = SHADOW_COLOR[color];
					ctx.shadowOffsetX = 0;
					ctx.shadowOffsetY = canvas.width * SHADOW_OFFSET_RATIO;
					ctx.shadowBlur = canvas.width * SHADOW_BLUR_RATIO;
					ctx.textAlign = "center";
					ctx.textBaseline = "middle";
					const cx = canvas.width / 2;
					const cy = canvas.height * (parseFloat(wallpaper.textPos.top) / 100);
					ctx.fillText(text, cx, cy);
				}

				const blob = await canvasToBlob(canvas);

				// 3) 以 multipart/form-data 上傳；後端只要圖檔，欄位名為 image
				//    （axios 遇到 FormData 會自動帶 multipart boundary）
				const formData = new FormData();
				formData.append("image", blob, OUTPUT_FILENAME);

				//    驗證 token 由各自的 axios instance 帶：
				//    LIFF 版帶會員 api_token、國際 web 版帶 web-client 的 access_token
				const res = isLiffFlow()
					? await api.post("/wallpaper/upload", formData)
					: await WallpaperWebApi.upload(formData);

				// 回應攔截器已回傳 res.data；相容 { image_url } 或 { data: { image_url } }
				const url =
					(res as { image_url?: string })?.image_url ??
					(res as { data?: { image_url?: string } })?.data?.image_url ??
					null;
				if (url) {
					setImageUrl(url);
					// 存進流程 context（sessionStorage）→ 分享頁 reload 後仍讀得到、關頁自動清
					flow.update({ resultImageUrl: url });
					setStatus("ready");
				} else {
					console.warn("[wallpaper-upload] 回傳沒有 image_url", res);
					setStatus("error");
				}
			} catch (err) {
				console.error("[wallpaper-upload] failed", err);
				setStatus("error");
			}
		})();
		// 僅進頁跑一次
		// eslint-disable-next-line react-hooks/exhaustive-deps

		logWallpaperPage({
			path: `/wallpaper/complete${langSuffix}`,
			from: getStoredFrom(),
		});
	}, []);

	// LINE 內建瀏覽器不支援開新分頁 / window.open，改在本頁彈全螢幕 overlay，
	// 放大圖片 + 長按儲存提示（長按存圖在各瀏覽器 / LINE WebView 都支援）。
	const handleDownload = () => {
		if (!imageUrl) return;
		logWallpaperEvent({
			category: "cta",
			path: `/wallpaper/complete${langSuffix}`,
			action: "download",
			event: "cta_download",
		});
		setShowSave(true);
	};

	const handleNext = () => {
		logWallpaperEvent({
			category: "cta",
			path: `/wallpaper/complete${langSuffix}`,
			action: "make",
			event: "cta_make",
		});
		// 合成圖 URL 已存進流程 context（sessionStorage），分享頁直接讀，不必再用 navigate state
		navigate({ pathname: "/wallpaper/share", search: location.search });
	};

	if (!wallpaper) return null;

	return (
		<Stack gap={0} align="center" w="100%" mt="1.5vw" pb="8vw">
			<Text
				size={locale === "en" ? "4vw" : "4.5vw"}
				c="#433931"
				fw={500}
				lh="normal"
				ta="center"
				style={{
					flexShrink: 0,
					letterSpacing: locale === "en" ? "-0.03em" : "0.1em",
				}}
			>
				{t("done.title")}
			</Text>

			{/* 顯示區：合成中顯示 loader，完成顯示後端回傳的 image_url */}
			<Box w="58vw" mt="4.5vw" style={{ aspectRatio: "0.462", flexShrink: 0 }}>
				{status === "ready" && imageUrl ? (
					<Image
						src={imageUrl}
						alt="tory burch wallpaper"
						w="100%"
						h="100%"
						fit="contain"
					/>
				) : (
					<Center w="100%" h="100%" bg="#F3EFE9">
						{status === "loading" ? (
							<Loader color="#433931" size="md" />
						) : (
							<Text c="#433931" size="3.5vw" ta="center" px="4vw">
								{t("done.error")}
							</Text>
						)}
					</Center>
				)}
			</Box>

			<Group grow gap="7.1vw" mt="8vw" w="85vw" wrap="nowrap">
				<CampaignButton
					variant="outline"
					disabled={status !== "ready"}
					onClick={handleDownload}
					fz={locale === "en" ? "3.3vw" : "4vw"}
					style={{ letterSpacing: locale === "en" ? "0.06em" : "0.2em" }}
					lh="normal"
				>
					{t("done.btn.download")}
				</CampaignButton>
				<CampaignButton
					variant="outline"
					onClick={handleNext}
					fz={locale === "en" ? "3.3vw" : "4vw"}
					style={{ letterSpacing: locale === "en" ? "0.06em" : "0.2em" }}
					lh="normal"
				>
					{t("done.btn.next")}
				</CampaignButton>
			</Group>

			{/* 離屏底圖節點：只放桌布圖，被 html2canvas 擷取；文字之後用原生 canvas 疊上 */}
			<div
				ref={compositeRef}
				aria-hidden
				style={{
					position: "fixed",
					top: 0,
					left: "-99999px",
					width: COMPOSITE_WIDTH,
					height: COMPOSITE_HEIGHT,
					overflow: "hidden",
					pointerEvents: "none",
				}}
			>
				<img
					src={wallpaper.src}
					crossOrigin="anonymous"
					alt=""
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						display: "block",
					}}
				/>
			</div>

			{/* 長按儲存 overlay：同頁 DOM，不開新分頁，LINE / 手機 / 桌面皆可用 */}
			{showSave && imageUrl && (
				<div
					role="dialog"
					aria-modal="true"
					onClick={() => setShowSave(false)}
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 2000,
						background: "rgba(0, 0, 0, 0.88)",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: "6vw",
						padding: "12vw 6vw",
					}}
				>
					<img
						src={imageUrl}
						alt="Tory Burch Wallpaper"
						// 點/長按圖片本身不關閉，避免長按存圖時誤觸關閉
						onClick={(e) => e.stopPropagation()}
						style={{
							maxHeight: "64vh",
							width: "auto",
							display: "block",
							flexShrink: 0,
						}}
					/>
					<Text
						c="#fff"
						size="3.8vw"
						ta="center"
						style={{ textDecoration: "underline", cursor: "pointer", letterSpacing: "0.1em" }}
					>
						{t("done.close")}
					</Text>
				</div>
			)}
		</Stack>
	);
}
